import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { VOCAB } from "../data/vocab";
import { recallProbability, getStatus } from "../hooks/useHLR";
import { useIsMobile } from "../hooks/useIsMobile";

export default function Dashboard({ wordStates, sessions, loading, onPracticeFiltered }) {
  const isMobile = useIsMobile();
  if (loading) return <div style={{ textAlign: "center", padding: 40, color: "#b0a898" }}>Loading…</div>;

  const now = Date.now() / 3600000;
  const words = Object.values(wordStates);
  const reviewed = words.filter(w => w.lastReviewTime > 0);
  const totalAns = words.reduce((s, w) => s + w.correctCount + w.incorrectCount, 0);
  const totalCorr = words.reduce((s, w) => s + w.correctCount, 0);
  const acc = totalAns ? Math.round((totalCorr / totalAns) * 100) : 0;

  const strongIds = reviewed.filter(w => recallProbability(w, now) >= 0.75).map(w => w.id);
  const atRiskIds = reviewed.filter(w => { const p = recallProbability(w, now); return p < 0.75 && p >= 0.4; }).map(w => w.id);
  const weakIds = reviewed.filter(w => recallProbability(w, now) < 0.4).map(w => w.id);

  const strong = strongIds.length;
  const atRisk = atRiskIds.length;
  const weak = weakIds.length;
  const unseen = VOCAB.length - reviewed.length;

  // Weekly bar chart data
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const today = new Date();
  const weekData = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (6 - i));
    const dayKey = d.toDateString();
    const count = (sessions || []).filter(s => {
      if (!s.date) return false;
      return new Date(s.date).toDateString() === dayKey;
    }).reduce((sum, s) => sum + (s.totalAnswers || 0), 0);
    return { day: days[d.getDay()], count, isToday: i === 6 };
  });

  const sessionData = (sessions || []).slice(-20).map((s, i) => ({
    session: i + 1,
    accuracy: parseFloat((s.accuracy * 100).toFixed(1)),
  }));

  const handleRowClick = (label, ids) => {
    if (ids.length === 0) return;
    if (onPracticeFiltered) onPracticeFiltered(label, ids);
  };

  // Combine all reviewed IDs for a mixed practice
  const allReviewedIds = reviewed.map(w => w.id);

  return (
    <div style={{ maxWidth: 960, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 900, color: "#111", marginBottom: 4, letterSpacing: -0.4 }}>Your Stats</h2>
          <p style={{ fontSize: 13, color: "#b0a898" }}>Track your Persian learning progress</p>
        </div>
        {allReviewedIds.length > 0 && (
          <button onClick={() => handleRowClick("All words", allReviewedIds)} style={{ display: "flex", alignItems: "center", gap: 7, background: "#1a8f68", color: "#fff", border: "none", borderRadius: 11, padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><path d="M3 8h10M9 4l4 4-4 4"/></svg>
            Practice all words
          </button>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
        <BigStat icon={<BookIcon />} bg="#fff5e0" iconColor="#e8900a" val={reviewed.length} lbl="Words seen" />
        <BigStat icon={<TargetIcon />} bg="#e8f7f1" iconColor="#1a8f68" val={`${acc}%`} lbl="Accuracy" />
        <BigStat icon={<StarIcon />} bg="#fff5e0" iconColor="#e8900a" val={totalAns} lbl="Total reviews" />
        <BigStat icon={<BoltIcon />} bg="#f0eeff" iconColor="#7c5cdb" val={strong} lbl="Strong words" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 12 }}>
        {/* Memory breakdown - clickable to start flashcard practice */}
        <div style={{ background: "#fff", borderRadius: 16, padding: "20px 22px", border: "1px solid #ede8df" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 4 }}>Memory breakdown</div>
          <div style={{ fontSize: 11, color: "#b0a898", marginBottom: 14 }}>Tap a row to practice those words as flashcards</div>
          <BreakdownRow label="Strong" val={strong} color="#1a8f68" total={VOCAB.length} clickable={strong > 0} onClick={() => handleRowClick("Strong words", strongIds)} />
          <BreakdownRow label="At risk" val={atRisk} color="#f0a020" total={VOCAB.length} clickable={atRisk > 0} onClick={() => handleRowClick("At risk words", atRiskIds)} />
          <BreakdownRow label="Needs practice" val={weak} color="#e24b4a" total={VOCAB.length} clickable={weak > 0} onClick={() => handleRowClick("Needs practice words", weakIds)} />
          <BreakdownRow label="Not started" val={unseen} color="#d5cfc5" total={VOCAB.length} clickable={false} />
        </div>

        {/* Weekly bar chart */}
        <div style={{ background: "#fff", borderRadius: 16, padding: "20px 22px", border: "1px solid #ede8df" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 4 }}>Words reviewed this week</div>
          <div style={{ fontSize: 11, color: "#b0a898", marginBottom: 14 }}>Last 7 days</div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 130, paddingBottom: 4 }}>
            {weekData.map((d, i) => {
              const maxCount = Math.max(...weekData.map(x => x.count), 1);
              const h = Math.max(8, (d.count / maxCount) * 100);
              return (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: d.count > 0 ? "#111" : "#c0b8ae" }}>{d.count}</div>
                  <div style={{ width: "100%", height: `${h}%`, background: d.isToday ? "#1a8f68" : d.count > 0 ? "#a8e0c8" : "#ede8df", borderRadius: "5px 5px 0 0", transition: "all 0.5s ease" }} />
                  <div style={{ fontSize: 10, color: "#b0a898", fontWeight: 600 }}>{d.day}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {sessionData.length > 1 ? (
        <div style={{ background: "#fff", borderRadius: 16, padding: "20px 22px", border: "1px solid #ede8df" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 16 }}>Accuracy over recent sessions</div>
          <ResponsiveContainer width="100%" height={170}>
            <AreaChart data={sessionData} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
              <defs>
                <linearGradient id="gAcc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1a8f68" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#1a8f68" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ede8df" />
              <XAxis dataKey="session" tick={{ fontSize: 10, fill: "#b0a898" }} />
              <YAxis tick={{ fontSize: 10, fill: "#b0a898" }} unit="%" domain={[0, 100]} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #ede8df", borderRadius: 10, fontSize: 12 }} formatter={v => [`${v}%`, "Accuracy"]} />
              <Area type="monotone" dataKey="accuracy" stroke="#1a8f68" fill="url(#gAcc)" strokeWidth={2.5} dot={{ fill: "#1a8f68", r: 3 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 16, padding: "32px 22px", border: "1px solid #ede8df", textAlign: "center" }}>
          <div style={{ fontSize: 13, color: "#b0a898" }}>Complete more sessions to see your accuracy trend.</div>
        </div>
      )}
    </div>
  );
}

function BreakdownRow({ label, val, color, total, clickable, onClick }) {
  return (
    <button
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      style={{
        width: "100%", marginBottom: 12, padding: "8px 10px", margin: "0 -10px 6px",
        background: clickable ? "transparent" : "transparent",
        border: "none", borderRadius: 10,
        cursor: clickable ? "pointer" : "default",
        fontFamily: "inherit", textAlign: "left", display: "block",
        transition: "background 0.15s",
      }}
      onMouseEnter={e => { if (clickable) e.currentTarget.style.background = "#f5f0e8"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: "#555", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
          {label}
          {clickable && (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
              <path d="M3 6h6M6.5 3l3 3-3 3"/>
            </svg>
          )}
        </span>
        <span style={{ fontSize: 13, color: "#111", fontWeight: 800 }}>{val}</span>
      </div>
      <div style={{ background: "#ede8df", borderRadius: 99, height: 7, overflow: "hidden" }}>
        <div style={{ width: `${(val / total) * 100}%`, height: "100%", borderRadius: 99, background: color, transition: "width 0.6s ease" }} />
      </div>
    </button>
  );
}

function BigStat({ icon, bg, iconColor, val, lbl }) {
  return (
    <div style={{ background: "#fff", borderRadius: 16, padding: "18px 20px", border: "1px solid #ede8df" }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: bg, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12, color: iconColor }}>{icon}</div>
      <div style={{ fontSize: 24, fontWeight: 900, color: "#111", letterSpacing: -0.5 }}>{val}</div>
      <div style={{ fontSize: 12, color: "#b0a898", marginTop: 2 }}>{lbl}</div>
    </div>
  );
}

function BookIcon() { return <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 3h9a1 1 0 011 1v10a1 1 0 01-1 1H3V3z"/><path d="M12 6h2a1 1 0 011 1v6a1 1 0 01-1 1h-2"/></svg>; }
function TargetIcon() { return <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="9" cy="9" r="7"/><circle cx="9" cy="9" r="3"/></svg>; }
function StarIcon() { return <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="9,1.5 11,6.5 16.5,7 12.5,11 14,16.5 9,13.5 4,16.5 5.5,11 1.5,7 7,6.5"/></svg>; }
function BoltIcon() { return <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 1L3 11h5l-1 6 7-10h-5l1-6z"/></svg>; }
